import { useEffect, useState } from "react";
import AccordionRow from "../../components/UIElements/Accordion/AccordionRow";
import "./Logs.css";
import { IRunData, LogFilters } from "../../interfaces/ILogs";
import { getAllLogs } from "../../services/logsService";
import PaginationComponent from "../../components/UIElements/Pagination/Pagination";
import { useLocation } from "react-router-dom";
import { PulseLoader } from "react-spinners";
import { toast, ToastContainer } from "react-toastify";
import LogsFilterBar from "./LogsFilterBar";

const Logs = () => {
  const location = useLocation();
  const MIN_ITEMS = parseInt(sessionStorage.getItem("itemsPerPage") || "10");

  const [runs, setRuns] = useState<IRunData[]>([]);
  const [logDate, setLogDate] = useState<Date | null>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [itemsPerPage] = useState(MIN_ITEMS);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("All");

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const fetchRuns = async (
    date: Date | null,
    pageNumber: number,
    tasks?: string[],
    files?: string[],
    statuses?: string[]
  ) => {
    try {
      setIsLoading(true);

      const filters: LogFilters = {
        date: date ? date.toISOString() : undefined,
        pageSize: itemsPerPage,
        pageNumber: pageNumber,
        tasks: tasks,
        files: files,
        status: statuses,
      };

      const response = await getAllLogs(filters);

      if (response.status === 200 && Array.isArray(response.data)) {
        localStorage.setItem("runs", JSON.stringify(response.data));
        setRuns(response.data);
        setTotalPages(response.totalPages || 1);
        setCurrentPage(pageNumber);
      } else {
        setRuns([]);
        setTotalPages(1);
        toast.info("No logs available.");
      }
    } catch (error) {
      console.error("Error fetching Runs:", error);
      toast.error("Error fetching logs");
    } finally {
      setIsLoading(false);
    }
  };

  // Main fetch useEffect — triggered only when filters change after initial load
  useEffect(() => {
    if (initialLoadDone) {
      fetchRuns(logDate, 1, selectedTasks, selectedFiles, selectedStatuses);
    }
  }, [selectedTasks, selectedFiles, selectedStatuses]);

  // Page change handler
  const handlePageChange = (pageNumber: number) => {
    fetchRuns(logDate, pageNumber, selectedTasks, selectedFiles, selectedStatuses);
  };

  // handle date change
  useEffect(() => {
    fetchRuns(logDate, 1,selectedTasks, selectedFiles, selectedStatuses );
  },[logDate])
  
  const handleClearFilter = () => {
    const today = new Date();
    setSelectedFiles([]);
    setSelectedTasks([]);
    setSelectedStatuses([]);
    setCurrentPage(1);
    setLogDate(today);
    fetchRuns(today, 1, [], [], []);
  };

  // One-time effect to handle navigation from dashboard
  useEffect(() => {
    let date = new Date();
    let tasks: string[] = [];
    let files: string[] = [];
    let statuses: string[] = [];

    if (location.state?.filter) {
      const filters = location.state.filter;
      tasks = filters.Tasks ? [filters.Tasks] : [];
      files = filters.Files ? [filters.Files] : [];
      statuses = filters.Status ? [filters.Status] : [];

      setSelectedTasks(tasks);
      setSelectedFiles(files);
      setSelectedStatuses(statuses);

      window.history.replaceState({}, document.title);
    }

    setInitialLoadDone(true);
    setLogDate(date);
    fetchRuns(date, 1, tasks, files, statuses);
  }, []);

  return (
    <>
      <div className="page-title">
        <p>Log Updates</p>
        <p className="page-subtitle">View all the logs.</p>
      </div>

      <LogsFilterBar
        selectedTimeFilter={selectedTimeFilter}
        setSelectedTimeFilter={setSelectedTimeFilter}
        logDate={logDate}
        setLogDate={setLogDate}
        fromDate={dateRange[0]}
        toDate={dateRange[1]}
        setDateRange={setDateRange}
        calendarVisible={calendarVisible}
        setCalendarVisible={setCalendarVisible}
        selectedTasks={selectedTasks}
        setSelectedTasks={setSelectedTasks}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        onClearFilter={handleClearFilter}
        onRefreshClick={() => fetchRuns(logDate, 1, selectedTasks, selectedFiles, selectedStatuses)}
      />

      <div className="container-fluid mt-2">
        <table className="table table-hover">
          <thead>
            <tr>
              <th className="th">Incident Time</th>
              <th className="th">Blob Name</th>
              <th className="th">Pipeline Name</th>
              <th className="th">Status</th>
              <th className="th text-center">Progress</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="text-center align-middle">
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "100px" }}>
                    <PulseLoader />
                  </div>
                </td>
              </tr>
            ) : runs.length > 0 ? (
              runs.map((log, index) => <AccordionRow key={index} log={log} />)
            ) : (
              <tr>
                <td colSpan={5} className="text-center">No logs available</td>
              </tr>
            )}
          </tbody>
        </table>

        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          closeOnClick={false}
          pauseOnHover
          draggable
          theme="light"
        />
      </div>
    </>
  );
};

export default Logs;
